import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { processReferralCommission } from '../utils/referralCommission';
import { doc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { OFFLINE_PAYMENT_DETAILS } from '../config/flutterwave';
import AIChatButton from '../components/AIChatButton';
import { shouldShowIOSWebViewAlert } from '../utils/deviceDetection';

const OfflinePaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { clearCart } = useCart();
  const [orderData, setOrderData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Simple useEffect that only runs once on mount
  useEffect(() => {
    const initializePage = async () => {
      // Check authentication
      if (!currentUser) {
        navigate('/signin');
        return;
      }

      // Get order data from localStorage
      try {
        const pendingOrderStr = localStorage.getItem('pendingOrder');
        if (pendingOrderStr) {
          const pendingOrder = JSON.parse(pendingOrderStr);
          setOrderData(pendingOrder);
          
          // Process referral commission if applicable - only after payment is successful
          if (pendingOrder.referredBy) {
            try {
              console.log('OfflinePaymentSuccess - Processing referral commission for referrer:', pendingOrder.referredBy);
              
              // Convert items to PaymentItem format for commission calculation
              const paymentItems = pendingOrder.items.map((item: any) => ({
                type: item.type,
                id: item.itemId,
                name: item.name,
                amount: item.price
              }));
              
              // Process the referral commission
              const commissionId = await processReferralCommission(
                pendingOrder.referredBy,
                pendingOrder.paymentId,
                pendingOrder.paymentId,
                paymentItems,
                pendingOrder.total
              );
              
              console.log('OfflinePaymentSuccess - Referral commission processed successfully. Commission ID:', commissionId);
            } catch (error) {
              console.error('OfflinePaymentSuccess - Error processing referral commission:', error);
              // Don't fail the page if commission processing fails
            }
          } else {
            console.log('OfflinePaymentSuccess - No referral commission to process (user was not referred)');
          }
          
          // Clear cart only once
          clearCart();
          
          // Remove from localStorage to prevent re-processing
          localStorage.removeItem('pendingOrder');
        } else {
          // No order data, redirect to cart
          navigate('/app/cart', { state: { from: location.pathname } });
          return;
        }
      } catch (error) {
        console.error('Error processing order data:', error);
        navigate('/app/cart', { state: { from: location.pathname } });
        return;
      }
      
      setIsLoading(false);
    };

    initializePage();
  }, []); // Empty dependency array - only runs once

  const handleWhatsAppRedirect = () => {
    if (!orderData) return;
    
    let message = `Hello! I have made payment for my order.

📋 ORDER DETAILS:
Payment ID: ${orderData.paymentId}
Amount: GHS ${orderData.total?.toFixed(2)}
Items: ${orderData.items?.length} item(s)

👤 CUSTOMER INFORMATION:
Name: ${orderData.customerInfo.firstName} ${orderData.customerInfo.lastName}
Email: ${orderData.customerInfo.email}
Phone: ${orderData.customerInfo.phone}
Address: ${orderData.customerInfo.address}, ${orderData.customerInfo.city}, ${orderData.customerInfo.state}, ${orderData.customerInfo.country}

🛒 ITEMS PURCHASED:`;

    // Add each item with details
    orderData.items?.forEach((item: any, index: number) => {
      message += `\n${index + 1}. ${item.name} (${item.type}) - GHS ${item.price?.toFixed(2)}`;
    });

    // Add referral information if available
    if (orderData.referredBy) {
      message += `\n\n🎯 Referred By: ${orderData.referredBy}`;
    }

    message += `\n\nPlease verify and grant access to my courses/books.`;
    
    try {
      // Use the standard WhatsApp API format that works reliably
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${OFFLINE_PAYMENT_DETAILS.whatsappNumber}&text=${encodeURIComponent(message)}`;
      
      // Try to open WhatsApp
      const newWindow = window.open(whatsappUrl, '_blank');
      
      // If window.open fails, try alternative approach
      if (!newWindow) {
        // Fallback: copy message to clipboard and show instructions
        navigator.clipboard.writeText(message).then(() => {
          alert(`WhatsApp couldn't open automatically. I've copied the message to your clipboard. Please:\n\n1. Open WhatsApp manually\n2. Send the copied message to ${OFFLINE_PAYMENT_DETAILS.whatsappNumber}`);
        }).catch(() => {
          // If clipboard fails, just show the message
          alert(`WhatsApp couldn't open automatically. Please manually send this message to ${OFFLINE_PAYMENT_DETAILS.whatsappNumber}:\n\n${message}`);
        });
      }
    } catch (error) {
      console.error('Error opening WhatsApp:', error);
      // Fallback: show message in alert
      alert(`Please manually send this message to ${OFFLINE_PAYMENT_DETAILS.whatsappNumber}:\n\n${message}`);
    }
  };

  // Loading state
  if (isLoading || !orderData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Payment Status */}
        <div className="text-center mb-8">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Offline Payment Initiated!</h1>
          <p className="text-lg text-gray-600">
            Your payment request has been recorded. Please complete the payment and notify us.
          </p>
        </div>

        {/* Payment Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Order Information</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="font-medium">Payment ID:</span> {orderData.paymentId}</p>
                <p><span className="font-medium">Date:</span> {new Date(orderData.timestamp).toLocaleDateString()}</p>
                <p><span className="font-medium">Total:</span> GHS {orderData.total?.toFixed(2)}</p>
                <p><span className="font-medium">Payment Method:</span> {orderData.offlinePaymentMethod}</p>
                <p><span className="font-medium">Status:</span> <span className="text-yellow-600 font-medium">Pending Verification</span></p>
                {orderData.referredBy && (
                  <p><span className="font-medium">Referred By:</span> <span className="text-blue-600">{orderData.referredBy}</span></p>
                )}
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                {shouldShowIOSWebViewAlert() ? "Items Added" : "Items Purchased"}
              </h3>
              <div className="space-y-2">
                {orderData.items?.map((item: any) => (
                  <div key={item.id} className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                    <div className="w-8 h-8 bg-primary-100 rounded flex items-center justify-center">
                      <span className="text-primary-600 text-sm font-medium">
                        {item.type === 'course' ? '📚' : item.type === 'book' ? '📖' : '🎥'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{item.type}</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">GHS {item.price?.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">Next Steps</h3>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-blue-600 mt-0.5" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-900">1. Complete Your Payment</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Use the payment details below to complete your transaction.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600 mt-0.5" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-900">2. Notify Us on WhatsApp</h4>
                <p className="text-sm text-blue-700 mt-1">
                  After making payment, click the button below to notify us on WhatsApp.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-blue-600 mt-0.5" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-900">3. Get Access</h4>
                <p className="text-sm text-blue-700 mt-1">
                  We'll verify your payment and grant access to your courses/books within 1-24 hours.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-medium text-yellow-900 mb-2">MTN Mobile Money</h4>
              <p className="text-sm text-yellow-800">
                <strong>Phone:</strong> {OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.phoneNumber}<br />
                <strong>Name:</strong> {OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.accountName}
              </p>
            </div>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">MoMo Pay</h4>
              <p className="text-sm text-blue-800">
                <strong>Phone:</strong> {OFFLINE_PAYMENT_DETAILS.momoPay.phoneNumber}<br />
                <strong>Name:</strong> {OFFLINE_PAYMENT_DETAILS.momoPay.accountName}
              </p>
            </div>
            
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">ABSA Bank</h4>
              <p className="text-sm text-green-800">
                <strong>Account:</strong> {OFFLINE_PAYMENT_DETAILS.absaBank.accountNumber}<br />
                <strong>Name:</strong> {OFFLINE_PAYMENT_DETAILS.absaBank.accountName}
              </p>
            </div>
          </div>
          
          {/* Copy All Payment Details Button */}
          <div className="text-center">
            <button
              onClick={() => {
                const allPaymentDetails = `Complete Payment Details:

MTN Mobile Money:
Phone: ${OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.phoneNumber}
Name: ${OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.accountName}

MoMo Pay:
Phone: ${OFFLINE_PAYMENT_DETAILS.momoPay.phoneNumber}
Name: ${OFFLINE_PAYMENT_DETAILS.momoPay.accountName}

ABSA Bank:
Account: ${OFFLINE_PAYMENT_DETAILS.absaBank.accountNumber}
Name: ${OFFLINE_PAYMENT_DETAILS.absaBank.accountName}

Your Order:
Payment ID: ${orderData.paymentId}
Amount: GHS ${orderData.total?.toFixed(2)}
Items: ${orderData.items?.length} item(s)
Date: ${new Date(orderData.timestamp).toLocaleDateString()}`;

                navigator.clipboard.writeText(allPaymentDetails).then(() => {
                  alert('All payment details copied to clipboard!');
                }).catch(() => {
                  // Fallback for older browsers
                  const textArea = document.createElement('textarea');
                  textArea.value = allPaymentDetails;
                  document.body.appendChild(textArea);
                  textArea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textArea);
                  alert('All payment details copied to clipboard!');
                });
              }}
              className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy All Payment Details
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleWhatsAppRedirect}
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
            Notify on WhatsApp
          </button>
          
          <Link
            to="/app/dashboard"
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Go to Dashboard
          </Link>
        </div>

        {/* Important Notice */}
        <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Important:</p>
              <p className="mt-1">
                Please keep your Payment ID ({orderData.paymentId}) for reference. 
                Access will be granted after payment verification.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default OfflinePaymentSuccess;
